import PortfolioRebalancingChart from './PortfolioRebalancingChart';
import PortfolioPerformanceChart from './PortfolioPerformanceChart';
import ResultsSummary from './ResultsSummary';
import { STATUS_MESSAGES } from './constants';

interface EventData {
  message: string;
  data?: any;
}

interface PortfolioChartProps {
  data?: EventData[];
  /** True while WebSocket simulation is connecting or streaming (no chart events yet). */
  simulationPending?: boolean;
  /** 0–100 progress percentage from ChartInstance. */
  progress?: number;
  /** Current WebSocket connection status from ChartInstance. */
  connectionStatus?: 'disconnected' | 'connecting' | 'connected' | 'completed' | 'error';
}

/**
 * PortfolioChart Component
 *
 * Wrapper component that displays two visualizations:
 * 1. Portfolio Performance - Line chart showing gains between events and accumulated gains
 * 2. Portfolio Rebalancing Summary - Line chart showing tickers over Decision dates
 *
 * @param {Object} props
 * @param {Array} props.data - Array of relevant events from WebSocket
 * @param {boolean} props.simulationPending - When true, show pending state instead of empty message
 */
const PortfolioChart: React.FC<PortfolioChartProps> = ({
  data = [],
  simulationPending = false,
  progress,
  connectionStatus,
}) => {
  if (!data || data.length === 0) {
    return (
      <div
        className={`flex flex-col items-center justify-center p-5 text-center ${
          simulationPending
            ? 'gap-4 italic text-muted-foreground'
            : 'text-foreground'
        }`}
        aria-busy={simulationPending || undefined}
      >
        {simulationPending && (
          <div
            className="size-9 rounded-full border-[3px] border-white/12 border-t-primary animate-spin shrink-0"
            role="status"
            aria-label="Loading portfolio data"
          />
        )}
        <p className="m-0">
          {simulationPending
            ? STATUS_MESSAGES.PORTFOLIO_CHART_PENDING
            : 'No portfolio data available'}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-5 p-5">
      <h2 className="mb-[30px] font-display text-foreground">
        Portfolio Analysis
      </h2>

      {/* Section 1: Results Summary */}
      <div className="mb-[30px]">
        <ResultsSummary data={data} progress={progress} connectionStatus={connectionStatus} />
      </div>

      {/* Section 2: Portfolio Performance - Gains Line Chart */}
      <div className="mb-[30px]">
        <PortfolioPerformanceChart data={data} />
      </div>

      {/* Section 3: Portfolio Rebalancing Summary - Line Chart */}
      <div>
        <PortfolioRebalancingChart data={data} />
      </div>
    </div>
  );
};

export default PortfolioChart;
